// Ubicación en el repo: src/components/OftSaleFormModal.jsx
// Modal Crear/Editar venta OFT — mismo contenido que openOftSaleModal() del
// prototipo (línea ~2606): paciente, fecha, ¿compró lentes? (sí/no), precio
// total (solo si compró), notas. Mismo patrón de buscador de paciente que
// PrescriptionFormModal/ExamFormModal.
import { useState } from 'react';
import { createOftsale, updateOftsale } from '../lib/oftsales';
import { searchPatients } from '../lib/patients';
import { formatPatientName } from '../lib/format';

function initialForm(sale) {
  if (sale) {
    return {
      patientId: sale.patient_id,
      patientLabel: sale.patients ? formatPatientName(sale.patients) : '',
      date: sale.date,
      compro: sale.compro,
      price: sale.price ?? '',
      notes: sale.notes || '',
    };
  }
  return {
    patientId: null,
    patientLabel: '',
    date: new Date().toLocaleDateString('sv-SE'),
    compro: true,
    price: '',
    notes: '',
  };
}

export default function OftSaleFormModal({ sale, actorId, onClose, onSaved }) {
  const isEdit = !!sale;
  const [form, setForm] = useState(() => initialForm(sale));
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function selectPatient(p) {
    setForm((f) => ({ ...f, patientId: p.id, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handlePatientSearch(value) {
    setPatientQuery(value);
    if (!value.trim()) {
      setPatientResults([]);
      return;
    }
    try {
      setPatientResults(await searchPatients(value));
    } catch {
      setPatientResults([]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.patientId) {
      setError('Selecciona un paciente.');
      return;
    }
    if (!form.date) {
      setError('Indica la fecha.');
      return;
    }
    setSaving(true);
    try {
      const values = {
        patient_id: form.patientId,
        date: form.date,
        compro: form.compro,
        price: form.compro && form.price !== '' ? Number(form.price) : null,
        notes: form.notes.trim() || null,
      };
      if (isEdit) {
        await updateOftsale(sale.id, values, actorId);
      } else {
        await createOftsale(values, actorId);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la venta.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        <h2>{isEdit ? 'Editar venta' : 'Nueva venta'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="field">
            <span>Paciente *</span>
            {form.patientId ? (
              <div className="patient-picker-selected">
                {form.patientLabel}
                {!isEdit && (
                  <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                    Cambiar
                  </button>
                )}
              </div>
            ) : (
              <>
                <input
                  value={patientQuery}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT…"
                />
                {patientResults.length > 0 && (
                  <ul className="patient-picker-results">
                    {patientResults.map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => selectPatient(p)}>
                          {formatPatientName(p)} {p.rut ? `— ${p.rut}` : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <label className="field">
            <span>Fecha *</span>
            <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
          </label>

          <label className="field">
            <span>¿El paciente compró lentes?</span>
            <select value={form.compro ? 'si' : 'no'} onChange={(e) => set('compro', e.target.value === 'si')}>
              <option value="si">Sí, compró lentes</option>
              <option value="no">No compró</option>
            </select>
          </label>

          {form.compro && (
            <label className="field">
              <span>Precio total (CLP)</span>
              <input type="number" value={form.price} onChange={(e) => set('price', e.target.value)} />
            </label>
          )}

          <label className="field">
            <span>Notas</span>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Observaciones…" />
          </label>

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
